/**
 * `/portal/operator/fleet` — the operator's drivers and vehicles, and its
 * drivers' access to the portal.
 *
 * Not a second copy of the forms: this is the public operator page's own
 * DriverManagementSection and FleetManagementSection, handed the portal's
 * authenticated `{ list, save, remove }` services instead of the link-based
 * ones. No operator id is passed — the API takes it from the signed-in
 * operator.
 *
 * Portal access is one more column on the drivers table: whether the driver
 * can sign in, and Invite / New link / Revoke. The hand-over after an invite
 * is the admin screen's own PortalAccessHandover (QR, WhatsApp, copy link),
 * in the portal's bottom sheet. The enrolment token lives only in this
 * screen's state while that sheet is open, and dies when it closes.
 */
import React, {
  useCallback, useEffect, useId, useState,
} from 'react';
import { Button } from 'react-bootstrap';
import DriverManagementSection from '../../OperatorPortal/Components/DriverManagementSection';
import FleetManagementSection from '../../OperatorPortal/Components/FleetManagementSection';
import PortalAccessHandover from '../../Operators/Components/PortalAccessHandover';
import BottomSheet from '../Components/BottomSheet';
import {
  operatorDriverService,
  operatorPortalUserService,
  operatorVehicleService,
} from '../../../Services/OperatorPortalApi';
import { operatorErrorMessage, operatorLoadErrorMessage } from './operatorFormat';
import Pill from './Pill';
import { driverLoginOf, LOGIN_STATES, loginState } from '../../../Utils/portalLogins';

/* --- portal access: state and copy --------------------------------------- */

// Which login is the driver's, and what state it is in: the shared rule
// (Utils/portalLogins.js), the same one the admin's assign dialog reads.

const ACCESS_LABELS = {
  [LOGIN_STATES.NONE]: { text: 'No access', tone: 'muted' },
  [LOGIN_STATES.INVITED]: { text: 'Invited', tone: 'warn', hint: 'Link not used yet' },
  [LOGIN_STATES.ACTIVE]: { text: 'Active', tone: 'ok' },
  [LOGIN_STATES.REVOKED]: { text: 'Revoked', tone: 'bad' },
};

/** Our own 400s are specific sentences worth showing; everything else is the portal's English. */
const accessErrorMessage = (failure) => {
  const data = failure?.response?.data;
  const specific = failure?.response?.status === 400
    && (data?.errors?.[0]?.message || data?.message);
  return specific || operatorErrorMessage(failure, {
    404: 'This driver is no longer one of your active drivers, or has no portal login yet. Refresh and try again.',
  });
};

/* --- the column ----------------------------------------------------------- */

function PortalAccessCell({
  driver, login, busy, onInvite, onRegenerate, onRevoke,
}) {
  const state = loginState(login);
  const label = ACCESS_LABELS[state];
  const name = driver.full_name;
  // The driver's name belongs in the accessible name — "Revoke access" alone
  // repeats once per row. It goes in `aria-label` rather than a
  // visually-hidden span so the button's text stays one line for anything
  // reading the rendered page (the label is still contained in the name,
  // which is what WCAG 2.5.3 asks for).
  const named = (action) => `${action} (${name})`;

  return (
    <div className="pp-op-access">
      <div className="pp-op-access__status">
        <Pill tone={label.tone}>{label.text}</Pill>
        {label.hint ? <span className="pp-op-small">{label.hint}</span> : null}
        {login?.email && state !== LOGIN_STATES.NONE ? (
          <span className="pp-op-small">{login.email}</span>
        ) : null}
      </div>
      <div className="pp-op-access__actions">
        {state === LOGIN_STATES.NONE || state === LOGIN_STATES.REVOKED ? (
          <Button
            variant="outline-primary"
            size="sm"
            aria-label={named('Invite to portal')}
            disabled={busy}
            onClick={() => onInvite(driver, login)}
          >
            Invite to portal
          </Button>
        ) : (
          <>
            <Button
              variant="outline-primary"
              size="sm"
              aria-label={named('New link')}
              disabled={busy}
              onClick={() => onRegenerate(driver)}
            >
              New link
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              aria-label={named('Revoke access')}
              disabled={busy}
              onClick={() => onRevoke(driver, login)}
            >
              Revoke access
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/* --- the sheets ----------------------------------------------------------- */

/** Asks for the driver's address — only when there is no earlier login to re-use. */
function InviteSheet({ driver, onClose, onInvited }) {
  const emailId = useId();
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    if (!email.trim()) {
      setError('Enter the driver\'s email address.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      onInvited(await operatorPortalUserService.grant({
        driverId: driver.Id,
        email: email.trim(),
      }));
    } catch (failure) {
      setError(accessErrorMessage(failure));
      setSaving(false);
    }
  };

  return (
    <BottomSheet
      title={`Invite ${driver.full_name} to the portal`}
      subtitle="They will see only the trips you assign to them — never prices."
      onClose={onClose}
      closeDisabled={saving}
      closeLabel="Close"
    >
      <form className="pp-op-form" onSubmit={submit} noValidate>
        <label className="pp-op-form__label" htmlFor={emailId}>Driver&apos;s email address</label>
        <input
          id={emailId}
          className="pp-op-form__select"
          type="email"
          inputMode="email"
          autoComplete="off"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={saving}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${emailId}-error` : undefined}
        />
        <p className="pp-op-form__hint">
          This is their portal login. They never need a password:
          {' '}
          you give them a single-use link next.
        </p>
        {error ? <div className="pp-op-error" role="alert" id={`${emailId}-error`}><span>{error}</span></div> : null}
        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? 'Creating the link…' : 'Create invite link'}
        </Button>
      </form>
    </BottomSheet>
  );
}

function RevokeSheet({
  name, login, onClose, onRevoked,
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const revoke = async () => {
    setSaving(true);
    setError('');
    try {
      await operatorPortalUserService.revoke(login.cognito_sub);
      onRevoked();
    } catch (failure) {
      setError(accessErrorMessage(failure));
      setSaving(false);
    }
  };

  return (
    <BottomSheet
      title={`Revoke ${name}'s portal access?`}
      subtitle={login.email}
      onClose={onClose}
      closeDisabled={saving}
      closeLabel="Close"
    >
      <div className="pp-op-form">
        <p className="pp-op-form__hint">
          They are signed out on their phone at once, and any link they have not used stops working.
          You can invite them again later.
        </p>
        {error ? <div className="pp-op-error" role="alert"><span>{error}</span></div> : null}
        <Button variant="danger" onClick={revoke} disabled={saving}>
          {saving ? 'Revoking…' : 'Revoke access'}
        </Button>
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
      </div>
    </BottomSheet>
  );
}

/* --- the page ------------------------------------------------------------- */

export default function OperatorFleet() {
  const [error, setError] = useState('');
  // The sections report the API's own message (English); keep the last one.
  const report = useCallback((message) => setError(message || ''), []);

  const [logins, setLogins] = useState([]);
  const [drivers, setDrivers] = useState(null);
  const [busyDriverId, setBusyDriverId] = useState(null);
  const [inviting, setInviting] = useState(null);
  const [revoking, setRevoking] = useState(null);
  // { token, expiresAt, user } — the one-time link, while its sheet is open.
  const [handover, setHandover] = useState(null);

  const loadLogins = useCallback(async () => {
    try {
      setLogins(await operatorPortalUserService.list());
    } catch (failure) {
      setError(operatorLoadErrorMessage(failure));
    }
  }, []);

  useEffect(() => { loadLogins(); }, [loadLogins]);

  const showHandover = (driver, result) => {
    setHandover({
      token: result.token,
      expiresAt: result.expiresAt,
      user: { kind: 'driver', name: driver.full_name, phone: driver.phone_number },
    });
    loadLogins();
  };

  // Straight to the server: a re-invite on the earlier address, or a new link.
  const runForDriver = async (driver, call) => {
    setBusyDriverId(driver.Id);
    setError('');
    try {
      showHandover(driver, await call());
    } catch (failure) {
      setError(accessErrorMessage(failure));
    } finally {
      setBusyDriverId(null);
    }
  };

  const invite = (driver, login) => {
    if (login?.email) {
      runForDriver(driver, () => operatorPortalUserService.grant({ driverId: driver.Id }));
    } else {
      setInviting(driver);
    }
  };

  const regenerate = (driver) => runForDriver(
    driver,
    () => operatorPortalUserService.regenerate(driver.Id),
  );

  const portalAccessColumn = {
    header: 'Portal access',
    render: (driver) => (
      <PortalAccessCell
        driver={driver}
        login={driverLoginOf(logins, driver.Id)}
        busy={busyDriverId === driver.Id}
        onInvite={invite}
        onRegenerate={regenerate}
        onRevoke={(row, login) => setRevoking({ name: row.full_name, login })}
      />
    ),
  };

  // A removed (deactivated) driver can't sign in — the API refuses the
  // login at the gate — but the login itself survives, and would work again
  // if the driver were reactivated. List those, so the operator can end them
  // for good.
  const listed = new Set((drivers || []).map((driver) => driver.Id));
  const removedWithAccess = drivers
    ? logins.filter((login) => login.active === 1 && !listed.has(login.driver_id))
    : [];

  return (
    <div className="pp-op-page">
      <div className="pp-op-page__head">
        <div>
          <h1 className="pp-op-title">Drivers &amp; vehicles</h1>
          <div className="pp-op-subtitle">
            The people and cars you can assign to your trips.
            {' '}
            Deactivated ones disappear from the lists.
          </div>
        </div>
      </div>

      {error ? (
        <div className="pp-op-error" role="alert">
          <span>{error}</span>
          <button type="button" className="pp-op-error__close" onClick={() => setError('')}>
            Dismiss
          </button>
        </div>
      ) : null}

      <section className="pp-op-card pp-op-fleet" aria-label="Drivers">
        <DriverManagementSection
          service={operatorDriverService}
          setError={report}
          extraColumn={portalAccessColumn}
          onDriversLoaded={setDrivers}
        />

        {removedWithAccess.length ? (
          <div className="pp-op-access-removed">
            <h3 className="pp-op-access-removed__title">Removed drivers who still have a portal login</h3>
            <p className="pp-op-small">
              They can&apos;t sign in while removed, but the login still exists.
              {' '}
              Revoke it to end it for good.
            </p>
            <ul className="pp-op-access-removed__list">
              {removedWithAccess.map((login) => {
                const name = login.driver_name || login.email;
                return (
                  <li key={login.cognito_sub}>
                    <span>
                      <strong>{name}</strong>
                      <span className="pp-op-small">{login.email}</span>
                    </span>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      aria-label={`Revoke access (${name})`}
                      onClick={() => setRevoking({ name, login })}
                    >
                      Revoke access
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </section>

      <section className="pp-op-card pp-op-fleet" aria-label="Vehicles">
        <FleetManagementSection service={operatorVehicleService} setError={report} />
      </section>

      {inviting ? (
        <InviteSheet
          driver={inviting}
          onClose={() => setInviting(null)}
          onInvited={(result) => {
            setInviting(null);
            showHandover(inviting, result);
          }}
        />
      ) : null}

      {revoking ? (
        <RevokeSheet
          name={revoking.name}
          login={revoking.login}
          onClose={() => setRevoking(null)}
          onRevoked={() => {
            setRevoking(null);
            loadLogins();
          }}
        />
      ) : null}

      {handover ? (
        <BottomSheet
          title={`Portal link for ${handover.user.name}`}
          subtitle="Let them scan the code, or send the link. It works once, for 24 hours."
          onClose={() => setHandover(null)}
          closeLabel="Close"
        >
          <PortalAccessHandover
            user={handover.user}
            token={handover.token}
            expiresAt={handover.expiresAt}
            stacked
          />
        </BottomSheet>
      ) : null}
    </div>
  );
}
